/**
 * Supervisor Assignment Details Page
 * Displays comprehensive assignment information and sites in the assigned zone
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { makeStyles, Button, Text, Spinner } from '@fluentui/react-components';
import { ArrowLeftRegular, PersonRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
  },
  header: {
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    minWidth: 'auto',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  assignmentHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px',
    borderBottom: '1px solid #e1dfdd',
    textAlign: 'center',
  },
  assignmentIcon: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    backgroundColor: '#f3f2f1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    border: '2px solid #e1dfdd',
    fontSize: '48px',
    color: '#605e5c',
  },
  assignmentTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
  },
  assignmentSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    padding: '24px',
  },
  section: {
    marginBottom: '0',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#605e5c',
    textTransform: 'uppercase',
  },
  fieldValue: {
    fontSize: '14px',
    color: '#323130',
  },
  sitesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  siteItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderRadius: '6px',
    backgroundColor: '#f9f9f9',
    border: '1px solid #e1dfdd',
  },
  siteDetails: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '8px',
  },
  siteField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  siteLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#605e5c',
    textTransform: 'uppercase',
  },
  siteValue: {
    fontSize: '13px',
    color: '#323130',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  fullWidthSection: {
    gridColumn: '1 / -1',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-block',
  },
  statusActive: {
    backgroundColor: '#dff6dd',
    color: '#107c10',
  },
  statusInactive: {
    backgroundColor: '#fde7e9',
    color: '#d13438',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#faf9f8',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '14px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tr: {
    borderBottom: '1px solid #edebe9',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '14px 16px',
    fontSize: '14px',
    color: '#323130',
  },
});

interface Zone {
  id: number;
  name: string;
}

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
}

interface Shift {
  id: number;
  name: string;
  start_time: string;
  end_time: string;
}

interface SupervisorAssignment {
  id: number;
  employee_id: number;
  supervisor_id?: number;
  zone_id: number;
  shift_id: number;
  start_date: string;
  end_date?: string;
  date_assigned: string;
  assignment_status: string;
  assigned_by?: number;
}

interface Site {
  id?: number | string;
  name: string;
  site_type_name?: string;
  region?: string;
  district?: string;
  suburb?: string;
}

export const SupervisorAssignmentDetails: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [assignment, setAssignment] = useState<SupervisorAssignment | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [zonesRes, employeesRes, shiftsRes] = await Promise.all([
        ApiClient.get('/get-zones'),
        ApiClient.get('/get-employee-data'),
        ApiClient.get('/get-shifts'),
      ]);

      if (zonesRes.data.status === 'success') {
        setZones(zonesRes.data.data || []);
      }
      if (employeesRes.data.status === 'success') {
        setEmployees(employeesRes.data.data || []);
      }
      if (shiftsRes.data.status === 'success') {
        setShifts(shiftsRes.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  }, []);

  const fetchAssignmentDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-assign-supervisors');
      if (response.data.status === 'success') {
        const assignments = response.data.data || [];
        const foundAssignment = assignments.find((a: SupervisorAssignment) => a.id === Number(id));

        if (foundAssignment) {
          setAssignment(foundAssignment);
        } else {
          newToast('error', 'Assignment not found');
          navigate('/account/guard-operations/assign-supervisor');
        }
      } else {
        newToast('error', response.data?.message || 'Failed to fetch assignment details');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  }, [id, navigate]);

  const fetchZoneSites = useCallback(async () => {
    if (!assignment?.zone_id) {
      return;
    }

    try {
      const response = await ApiClient.get(`/get-zone-sites?zone_id=${assignment.zone_id}`);
      if (response.data.status === 'success') {
        setSites(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  }, [assignment]);

  useEffect(() => {
    fetchReferenceData();
    fetchAssignmentDetails();
  }, [fetchReferenceData, fetchAssignmentDetails]);

  useEffect(() => {
    if (assignment) {
      fetchZoneSites();
    }
  }, [assignment, fetchZoneSites]);

  const handleBack = () => {
    navigate('/account/guard-operations/assign-supervisor');
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) {
      return '-';
    }

    const cleanDate = dateString.split(' ')[0] || '';
    const date = new Date(cleanDate);

    if (isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const getZoneName = (zoneId: number) => {
    const zone = zones.find(z => z.id === zoneId);
    return zone?.name || `Zone ${zoneId}`;
  };

  const getEmployeeName = (employeeId: number) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : `Employee ${employeeId}`;
  };

  const getShiftName = (shiftId: number) => {
    const shift = shifts.find(s => s.id === shiftId);
    return shift?.name || `Shift ${shiftId}`;
  };

  const getShift = (shiftId: number) => {
    return shifts.find(s => s.id === shiftId);
  };

  const calculateShiftHours = (startTime?: string, endTime?: string): string => {
    if (!startTime || !endTime) {
      return '-';
    }

    try {
      const [startHour = 0, startMin = 0] = startTime.split(':').map(Number);
      const [endHour = 0, endMin = 0] = endTime.split(':').map(Number);

      let hours = endHour - startHour;
      let minutes = endMin - startMin;

      if (minutes < 0) {
        hours -= 1;
        minutes += 60;
      }

      if (hours < 0) {
        hours += 24;
      }

      return minutes > 0 ? `${hours}.${Math.round((minutes / 60) * 10)} hours` : `${hours} hours`;
    } catch {
      return '-';
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Spinner size="large" label="Loading assignment details..." />
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Text size={500} weight="semibold">
            Assignment not found
          </Text>
          <Button appearance="primary" onClick={handleBack} style={{ marginTop: '16px' }}>
            Back to Assignments
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            icon={<ArrowLeftRegular />}
            appearance="subtle"
            onClick={handleBack}
            className={styles.backButton}
          />
          <Text className={styles.pageTitle}>Supervisor Assignment Details</Text>
        </div>
      </div>

      {/* Assignment Card */}
      <ContentCard>
        {/* Assignment Header */}
        <div className={styles.assignmentHeader}>
          <div className={styles.assignmentIcon}>
            <PersonRegular />
          </div>
          <Text className={styles.assignmentTitle}>
            {getEmployeeName(assignment.supervisor_id || assignment.employee_id)}
          </Text>
        </div>

        {/* Assignment Details Grid */}
        <div className={styles.contentGrid}>
          {/* Zone & Shift Information */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Zone & Shift</Text>
            <div className={styles.fieldGroup}>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Zone</Text>
                <Text className={styles.fieldValue}>{getZoneName(assignment.zone_id)}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Shift</Text>
                <Text className={styles.fieldValue}>{getShiftName(assignment.shift_id)}</Text>
              </div>
              {getShift(assignment.shift_id)?.start_time && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Start Time</Text>
                  <Text className={styles.fieldValue}>
                    {getShift(assignment.shift_id)?.start_time}
                  </Text>
                </div>
              )}
              {getShift(assignment.shift_id)?.end_time && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>End Time</Text>
                  <Text className={styles.fieldValue}>
                    {getShift(assignment.shift_id)?.end_time}
                  </Text>
                </div>
              )}
              {getShift(assignment.shift_id)?.start_time &&
                getShift(assignment.shift_id)?.end_time && (
                  <div className={styles.field}>
                    <Text className={styles.fieldLabel}>Hours</Text>
                    <Text className={styles.fieldValue}>
                      {calculateShiftHours(
                        getShift(assignment.shift_id)?.start_time,
                        getShift(assignment.shift_id)?.end_time
                      )}
                    </Text>
                  </div>
                )}
            </div>
          </div>

          {/* Duration Information */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Duration</Text>
            <div className={styles.fieldGroup}>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Start Date</Text>
                <Text className={styles.fieldValue}>{formatDate(assignment.start_date)}</Text>
              </div>
              {assignment.end_date && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>End Date</Text>
                  <Text className={styles.fieldValue}>{formatDate(assignment.end_date)}</Text>
                </div>
              )}
            </div>
          </div>

          {/* Sites in Zone */}
          <div className={`${styles.section} ${styles.fullWidthSection}`}>
            <Text className={styles.sectionTitle}>Sites in Zone ({sites.length})</Text>
            {sites.length === 0 ? (
              <Text style={{ color: '#605e5c', fontSize: '14px' }}>No sites in this zone</Text>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className={styles.table}>
                  <thead className={styles.tableHeader}>
                    <tr>
                      <th className={styles.th}>Site Name</th>
                      <th className={styles.th}>Type</th>
                      <th className={styles.th}>Region</th>
                      <th className={styles.th}>District</th>
                      <th className={styles.th}>Suburb</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sites.map(site => (
                      <tr key={site.id} className={styles.tr}>
                        <td className={styles.td}>{site.name || '—'}</td>
                        <td className={styles.td}>{site.site_type_name || '—'}</td>
                        <td className={styles.td}>{site.region || '—'}</td>
                        <td className={styles.td}>{site.district || '—'}</td>
                        <td className={styles.td}>{site.suburb || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </ContentCard>
    </div>
  );
};

export default SupervisorAssignmentDetails;
